import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  SortingState,
  ColumnFiltersState,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Plus, Loader2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useQueryState, useQueryStates, parseAsString, parseAsInteger, createParser } from 'nuqs';
import { PaginatedResponse, ListQueryParams } from "@/app/types/api";
import { Task, TaskStatus, TaskUpdate } from "@/app/types/task";
import { taskService } from "@/app/services/taskService";
import { listUsers } from "@/app/services/userManagementService";
import { listTeams } from "@/app/services/teamService";
import { useAuthStore } from "@/app/store/authStore";
import { useTaskStore } from "@/app/store/taskStore";
import { useSQLiteSync } from "@/hooks/useSQLiteSync";
import { getItemByIdAsync } from "@/lib/indexeddb/database";
import { columns as baseColumns } from "../components/columns";
import TaskDataTable from "../components/TaskDataTable";
import { CreateTaskModal } from "../components/CreateTaskModal";
import { TaskForm } from "../components/TaskForm";
import { toast } from "sonner";
import { startOfDay, endOfDay } from "date-fns";

// Define selectors for auth store
const selectUser = (state: { user: any }): any => state.user;

// Custom parser for sorting state
const sortingParser = createParser({
  parse: (value: string) => {
    if (!value) return []
    const [field, direction] = value.split('-')
    return [{
      id: field,
      desc: direction === 'desc'
    }]
  },
  serialize: (value: SortingState) => {
    if (!value.length) return ''
    const sort = value[0]
    return `${sort.id}-${sort.desc ? 'desc' : 'asc'}`
  }
})

// Custom parser for page (0-based internal, 1-based in URL)
const pageParser = createParser({
  parse: (value: string) => {
    const page = parseInt(value, 10)
    return isNaN(page) || page < 1 ? 0 : page - 1 // Convert 1-based to 0-based
  },
  serialize: (value: number) => {
    return String(value + 1) // Convert 0-based to 1-based
  }
})

/**
 * Task List Page (Client Manager)
 *
 * Displays list of tasks assigned to or created by the current user
 * Backend filters tasks based on user role and permissions
 *
 * Features:
 * - TanStack Table with server-side pagination, sorting, filtering
 * - URL state management with nuqs
 * - Filter by title, status, priority, assigned user
 * - Sort by any column
 * - View task details
 * - Mark tasks as complete
 */
const TaskListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openCreateModal, setDefaultTaskType } = useTaskStore();
  const [searchParams] = useSearchParams();
  const taskType = searchParams.get('task_type');

  const pageConfig = useMemo(() => {
    switch (taskType) {
      case 'todo':
        return { title: 'To-Do', desc: 'Manage your to-do items', addLabel: 'New To-Do' }
      case 'enquiry':
        return { title: 'Enquiries', desc: 'Manage customer enquiries and requests', addLabel: 'New Enquiry' }
      case 'appointment':
        return { title: 'Appointments', desc: 'Manage scheduled appointments', addLabel: 'New Appointment' }
      default:
        return { title: 'Tasks', desc: 'Manage your tasks, follow-ups, and reminders', addLabel: 'New Task' }
    }
  }, [taskType]);

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Use nuqs for URL state management
  const [page, setPage] = useQueryState('page', pageParser.withDefault(0))
  const [sorting, setSorting] = useQueryState('sortBy', sortingParser.withDefault([]))

  // Use nuqs for filter state management
  const [filterStates, setFilterStates] = useQueryStates({
    title: parseAsString,
    status: parseAsString,
    priority: parseAsString,
    assigned_to_user_id: parseAsString,
    related_entity_id: parseAsInteger,
  }, {
    // Use shallow routing to prevent full page reloads
    shallow: true,
  })

  // Service filter state
  const { isInitialized: isIndexedDBInitialized } = useSQLiteSync();
  const [selectedServiceName, setSelectedServiceName] = useState<string | null>(null);

  // Resolve item name from IndexedDB when page loads with a pre-selected filter
  useEffect(() => {
    if (filterStates.related_entity_id && isIndexedDBInitialized) {
      getItemByIdAsync(filterStates.related_entity_id).then(item => {
        setSelectedServiceName(item?.name || `Item #${filterStates.related_entity_id}`);
      });
    } else if (!filterStates.related_entity_id) {
      setSelectedServiceName(null);
    }
  }, [filterStates.related_entity_id, isIndexedDBInitialized]);

  // Build columnFilters from nuqs state for compatibility with existing code
  const nonColumnFilterKeys = ['related_entity_id'];
  const columnFilters = useMemo<ColumnFiltersState>(() => {
    const filters: ColumnFiltersState = [];
    Object.entries(filterStates).forEach(([key, value]) => {
      if (nonColumnFilterKeys.includes(key)) return;
      if (value !== null && value !== undefined && value !== '') {
        filters.push({ id: key, value });
      }
    });
    return filters;
  }, [filterStates]);

  const [pageSize] = useState(10);
  const pagination = {
    pageIndex: page,
    pageSize,
    pageCount: 0
  };

  // Auth state
  const user = useAuthStore(selectUser);

  // Get organization ID from user data
  const userOrgId = useMemo(() => user?.organization_id || user?.organization?.id, [user]);

  // Fetch users for assigned_to filter
  const { data: usersData } = useQuery({
    queryKey: ['users', { limit: 100 }],
    queryFn: () => listUsers({ limit: 100, skip: 0 }, userOrgId),
    enabled: !!userOrgId,
  });

  // Build user options for filter
  const userOptions = useMemo(() => {
    if (!usersData?.items) return [];
    return usersData.items.map(user => ({
      label: user.full_name || user.username,
      value: String(user.id),
    }));
  }, [usersData]);

  // Fetch teams for the team filter
  const { data: teamsData } = useQuery({
    queryKey: ['teams-list'],
    queryFn: () => listTeams({ limit: 100 }),
    enabled: !!userOrgId,
  });

  const teamOptions = useMemo(() => {
    if (!teamsData?.items) return [];
    return teamsData.items.map(team => ({
      label: team.name,
      value: String(team.id),
    }));
  }, [teamsData]);

  // Construct query parameters for the API
  const queryParams = useMemo<ListQueryParams>(() => {
    const params: ListQueryParams = {
      skip: page * pageSize,
      limit: pageSize,
    };

    // Add sorting if defined
    if (sorting.length > 0) {
      params.sort_by = sorting[0].id;
      params.sort_dir = sorting[0].desc ? "desc" : "asc";
    }

    // Add filters if defined
    if (columnFilters.length > 0) {
      const filtersList: any[] = [];
      columnFilters.forEach((filter) => {
        if (filter.value !== undefined && filter.value !== null && filter.value !== '') {
          // Use icontains for title field to enable wildcard search
          const textFields = ['title'];
          const operator = textFields.includes(filter.id) ? 'icontains' : 'eq';

          filtersList.push({
            field: filter.id,
            op: operator,
            value: filter.value
          });
        }
      });

      if (filtersList.length > 0) {
        params.filters = JSON.stringify(filtersList);
      }
    }

    // Add task_type as a direct query parameter (not in filters array)
    if (taskType) {
      (params as any).task_type = taskType;
    }

    // "My team pool" view (from the dashboard TeamPoolWidget) — unclaimed tasks in my teams
    if (searchParams.get('team_pool') === '1') {
      (params as any).unassigned_in_my_teams = true;
    }

    // "Due today" deep-link (from dashboard "Due Today" panels)
    if (searchParams.get('due') === 'today') {
      const now = new Date();
      (params as any).due_date_from = startOfDay(now).toISOString();
      (params as any).due_date_to = endOfDay(now).toISOString();
    }

    // Add service (item) filter as direct query parameters
    if (filterStates.related_entity_id) {
      (params as any).related_entity_type = 'item';
      (params as any).related_entity_id = filterStates.related_entity_id;
    }

    return params;
  }, [page, pageSize, sorting, columnFilters, taskType, filterStates.related_entity_id, searchParams]);

  // Query to fetch tasks
  const {
    data: tasksResponse = { items: [], total: 0 },
    isLoading: isTasksLoading,
    isError: isTasksError,
    error: tasksError,
    refetch: refetchTasks,
  } = useQuery<PaginatedResponse<Task>>({
    queryKey: ['tasks', userOrgId, queryParams],
    queryFn: () => {
      if (!userOrgId) {
        throw new Error('No organization ID available');
      }
      return taskService.list(queryParams);
    },
    enabled: !!userOrgId,
  });

  // Calculate page count from response
  const pageCount = useMemo(() => {
    if (tasksResponse) {
      return Math.ceil(tasksResponse.total / pageSize);
    }
    return 0;
  }, [tasksResponse, pageSize]);

  // Mark task as complete mutation
  const markCompleteMutation = useMutation({
    mutationFn: (taskId: number) => taskService.markComplete(taskId),
    onSuccess: (updatedTask) => {
      toast.success('Task completed', {
        description: `"${updatedTask.title}" has been marked as completed.`,
      });

      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to mark task as complete';

      toast.error('Failed to update task', {
        description: errorMessage,
      });
    },
  });

  // Claim a team-pool task (assign to self)
  const claimMutation = useMutation({
    mutationFn: (taskId: number) => taskService.claim(taskId),
    onSuccess: (updatedTask) => {
      toast.success('Task claimed', {
        description: `"${updatedTask.title}" is now assigned to you.`,
      });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error: any) => {
      toast.error('Could not claim task', {
        description: error?.response?.data?.detail || error?.message || 'Failed to claim task',
      });
    },
  });

  // Release a claimed task back to its team pool
  const releaseMutation = useMutation({
    mutationFn: (taskId: number) => taskService.release(taskId),
    onSuccess: (updatedTask) => {
      toast.success('Task released', {
        description: `"${updatedTask.title}" is back in the team pool.`,
      });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (error: any) => {
      toast.error('Could not release task', {
        description: error?.response?.data?.detail || error?.message || 'Failed to release task',
      });
    },
  });

  // Update task mutation
  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: TaskUpdate }) => taskService.update(id, data),
    onSuccess: (updatedTask) => {
      toast.success('Task updated successfully', {
        description: `"${updatedTask.title}" has been updated.`,
      });

      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['tasks'] });

      // Close edit modal
      setIsEditModalOpen(false);
      setSelectedTask(null);
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update task';

      toast.error('Failed to update task', {
        description: errorMessage,
      });
    },
  });

  // Setup event listeners for task actions
  React.useEffect(() => {
    const handleCompleteTask = (event: CustomEvent<Task>) => {
      markCompleteMutation.mutate(event.detail.id);
    };
    const handleClaimTask = (event: CustomEvent<Task>) => {
      claimMutation.mutate(event.detail.id);
    };
    const handleReleaseTask = (event: CustomEvent<Task>) => {
      releaseMutation.mutate(event.detail.id);
    };
    const handleViewTask = (event: CustomEvent<Task>) => {
      navigate(`/tasks/detail/${event.detail.id}`);
    };

    document.addEventListener('complete-task', handleCompleteTask as EventListener);
    document.addEventListener('claim-task', handleClaimTask as EventListener);
    document.addEventListener('release-task', handleReleaseTask as EventListener);
    document.addEventListener('view-task', handleViewTask as EventListener);

    return () => {
      document.removeEventListener('complete-task', handleCompleteTask as EventListener);
      document.removeEventListener('claim-task', handleClaimTask as EventListener);
      document.removeEventListener('release-task', handleReleaseTask as EventListener);
      document.removeEventListener('view-task', handleViewTask as EventListener);
    };
  }, [markCompleteMutation, claimMutation, releaseMutation, navigate]);

  // Context-aware create modal
  const handleOpenCreateModal = useCallback(() => {
    setDefaultTaskType(taskType || undefined);
    openCreateModal();
  }, [taskType, openCreateModal, setDefaultTaskType]);

  // Handle row click - open detail page in the same window (no new tab)
  const handleRowClick = useCallback((task: Task) => {
    navigate(`/tasks/detail/${task.id}`);
  }, [navigate]);

  // Handle edit form submit
  const handleEditSubmit = useCallback((data: TaskUpdate) => {
    if (selectedTask) {
      updateTaskMutation.mutate({ id: selectedTask.id, data });
    }
  }, [selectedTask, updateTaskMutation]);

  // Event Handlers
  const handlePaginationChange = (newPagination: { pageIndex: number; pageSize: number }) => {
    setPage(newPagination.pageIndex);
  };

  // Handle filter changes
  const handleFilterChange = useCallback((filters: ColumnFiltersState) => {
    // Build the new filter states for nuqs
    const updates: Record<string, string | null> = {};

    // List of all possible filter keys
    const knownFilterKeys = ['title', 'status', 'priority', 'assigned_to_user_id'];

    // First, reset all known keys to null
    knownFilterKeys.forEach(key => {
      updates[key] = null;
    });

    // Then add the active filters
    filters.forEach(filter => {
      const value = filter.value;
      updates[filter.id] = value !== undefined && value !== null && value !== '' ? String(value) : null;
    });

    // Update filters using nuqs
    setFilterStates(updates);

    // Reset to first page when filters change
    setPage(0);
  }, [setFilterStates, setPage]);

  // Handle service filter change
  const handleServiceFilterChange = useCallback((itemId: number | null, itemName: string | null) => {
    setFilterStates(prev => ({
      ...prev,
      related_entity_id: itemId,
    }));
    setSelectedServiceName(itemName);
    setPage(0);
  }, [setFilterStates, setPage]);

  // Handle error state
  if (isTasksError && !!userOrgId) {
    return (
      <div className="py-8 flex flex-col items-center justify-center space-y-4 text-center">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <p className="text-xl font-semibold text-destructive">Error Loading Tasks</p>
        <p className="text-sm text-muted-foreground">
          {tasksError instanceof Error ? tasksError.message : 'An unknown error occurred.'}
        </p>
        <button onClick={() => refetchTasks()} className="text-sm underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{pageConfig.title}</h1>
          <p className="text-muted-foreground mt-1">
            {pageConfig.desc}
            {tasksResponse && ` · ${tasksResponse.total} total`}
          </p>
        </div>
        <Button onClick={handleOpenCreateModal} className="gap-2">
          <Plus className="h-4 w-4" />
          {pageConfig.addLabel}
        </Button>
      </div>

      {/* Tasks Data Table */}
      <TaskDataTable
        data={tasksResponse}
        columns={baseColumns}
        isLoading={isTasksLoading}
        pagination={{...pagination, pageCount}}
        sorting={sorting}
        columnFilters={columnFilters}
        onPaginationChange={handlePaginationChange}
        onSortingChange={setSorting}
        onColumnFiltersChange={handleFilterChange}
        userOptions={userOptions}
        teamOptions={teamOptions}
        onRowClick={handleRowClick}
        selectedServiceId={filterStates.related_entity_id ?? null}
        selectedServiceName={selectedServiceName}
        onServiceFilterChange={handleServiceFilterChange}
      />

      {/* Create Task Modal */}
      <CreateTaskModal />

      {/* Edit Task Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
            <DialogDescription>
              Update task details, status, or assignment.
            </DialogDescription>
          </DialogHeader>

          {selectedTask && (
            <TaskForm
              mode="edit"
              defaultValues={selectedTask}
              onSubmit={handleEditSubmit}
              isLoading={updateTaskMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskListPage;
